import {Injectable} from '@angular/core';
import {Platform, Events} from "ionic-angular";
import {Network} from "@ionic-native/network";
import {Observable} from "rxjs/Observable";
import {Observer} from "rxjs/Observer";
import {SQLite, SQLiteObject} from "@ionic-native/sqlite";
import {VizixService} from "./vizix.service";
import {PatchThingRequestItem} from "../models/PatchThingRequestItem";
import {VizixClientService} from "vizixc/dist";
import {ErrorResponse} from "../models/ErrorResponse";
import {
  CYCLE_COUNT_ALREADY_UPLOADING,
  END_POINT_MODE_THING_BRIDGE,
  END_POINT_MODE_THINGS, ErrorCode,
  ITEM_EVENT_CYCLE_COUNT, ITEM_UDF_ACTIVITY, ITEM_UDF_CATEGORY, ITEM_UDF_CYCLE_COUNT_DATE, ITEM_UDF_LAST_DETECT_TIME,
  ITEM_UDF_MOBILE_SYNC_SESSION_ID,
  ITEM_UDF_SOURCE, ITEM_UDF_SUB_CATEGORY, ITEM_UDF_TS_EDGE_IN, ITEM_UDF_USER,
  ITEM_UDF_UUID_MOBILE,
  ITEM_UDF_ZONE,
  NO_INTERNET_CONNECTION,
  THING_BRIDGE_SOURCE_CYCLE_COUNT,
  UNKNOWN_ERROR,
  UPLOAD_STATUS_PENDING_REMOVE,
  UPLOAD_STATUS_PENDING_UPLOAD, VIZIX_THING_ITEM_UDF_SOURCE,
} from "../app/app.constants";
import {LastDetectTag} from "../models/LastDetectTag";
import {DepartmentsZones, ZoneType} from "../models/zoneDeptRelation";
import {TableScriptReportResponse} from "../models/TableScriptReportResponse";
import {Subscription} from "rxjs/Subscription";

@Injectable()
export class CycleCountService {

  private tag = '[CYCLE_COUNT_SERVICE]';

  private isConnected: boolean = false;
  private isUploading: boolean = false;
  private CYCLE_COUNT_DB_NAME = 'CycleCount2.db'; // Version 2
  private CYCLE_COUNT_DB_LOCATION = 'default';
  private CYCLE_COUNT_DB_TABLE_LOCAL = 'localCycleCount';
  private db: SQLiteObject; // Local db
  public uploadProgress = 0;
  private retrySyncInterval = 120;
  private retrySyncAttempts = 5;
  private connectionAttempts = 0;
  public pendingCycleCountItemCount: number = 0;
  private retryPendingTimer: any;
  private pendingRemoveSaveEnabled: boolean = false; // TODO: ENABLE WHEN "FINISHED" EVENT IS IMPLEMENTED AND RETURNED BY VIZIX
  private isCordova: boolean = false;
  public isGettingPendingCount: boolean = false;
  public thingTypeCodeToSyncGroup: string = '';
  private TABLE_COLUMN_EPC = 'epc';
  private TABLE_COLUMN_LAST_DETECT_TIME = 'lastDetectTime';
  private TABLE_COLUMN_ZONE = 'zone';
  private TABLE_COLUMN_USERNAME = 'username';
  private TABLE_COLUMN_UUID = 'uuid';
  private TABLE_COLUMN_SESSION_ID = 'sessionId';
  private TABLE_COLUMN_STATUS = 'status';
  private disconnectedSubscription: Subscription;
  private connectedSubscription: Subscription;

  constructor(public platform: Platform,
              private network: Network,
              private sqlite: SQLite,
              private vizixService: VizixService,
              private vizixClientService: VizixClientService,
              private events: Events) {
  }

  public init() {
    console.log(this.tag, 'Initializing');

    // Init database and table if table not exists
    //  CC Table columns:
    //    epc | lastDetectTime | zone | username | uuid | sessionID | status
    //
    // status -> pending (Waiting for confirmation to remove, do not upload again)
    //
    //  Events Table columns:
    //    event

    if (this.platform.is('cordova') && !this.platform.is('core')) {
      this.isCordova = true;
      console.log(this.tag, 'Platform is mobile, init db');

      this.sqlite.create({
        name: this.CYCLE_COUNT_DB_NAME,
        location: this.CYCLE_COUNT_DB_LOCATION
      }).then((db: SQLiteObject) => {
        this.db = db;
        this.db.executeSql('create table if not exists ' + this.CYCLE_COUNT_DB_TABLE_LOCAL +
          '(' + this.TABLE_COLUMN_EPC + ' VARCHAR(32), ' +
          this.TABLE_COLUMN_LAST_DETECT_TIME + ' INT, ' +
          this.TABLE_COLUMN_ZONE + ' VARCHAR(64), ' +
          this.TABLE_COLUMN_USERNAME + ' VARCHAR(64), ' +
          this.TABLE_COLUMN_UUID + ' VARCHAR(64), ' +
          this.TABLE_COLUMN_SESSION_ID + ' INT, ' +
          this.TABLE_COLUMN_STATUS + ' VARCHAR(64))', {})
          .then(() => {
            console.log(this.tag, 'table ' + this.CYCLE_COUNT_DB_TABLE_LOCAL + ' created');
            this.initService();
          })
          .catch(e => {
            console.log(this.tag, e);
            this.isCordova = false;
          });
      }).catch(e => {
        console.log(this.tag, e);
        this.isCordova = false;
      });
    } else {
      console.log(this.tag, 'Platform is not mobile, skipping database');
      this.isCordova = false;
      this.initService();
    }
  }

  public deleteDataBase() {
    if (this.isCordova) {
      this.sqlite.deleteDatabase({
        name: this.CYCLE_COUNT_DB_NAME,
        location: this.CYCLE_COUNT_DB_LOCATION
      }).then(() => {
        console.log(this.tag, ' Delete DB cycleCount successful');
      }, (err) => {
        console.log(this.tag, ' Error Delete DB cycleCount', err);
      });
    } else {
      console.log(this.tag, 'Cant delete database, platform is not cordova!');
    }
  }

  public initService() {

    this.getPendingCount();

    this.updateThingTypeCodeToSyncGroup();

    this.retrySyncInterval = this.vizixService.configurationApp.CycleCountConfiguration.RetrySyncInterval.value;
    this.retrySyncAttempts = this.vizixService.configurationApp.CycleCountConfiguration.RetrySyncAttempts.value;
    console.log(this.tag, 'Retry Sync Interval', this.retrySyncInterval);
    console.log(this.tag, 'Retry Sync Attempts', this.retrySyncAttempts);

    // Check if network connection is available
    if (this.network.type != 'none' && this.network.type != 'unknown') {
      // If available, then check if pending cycle counts
      console.log(this.tag, 'network state: ' + this.network.type);
      this.isConnected = true;
      setTimeout(() => { // Wait 5 seconds on service startup to check if pending items (to init db)
        this.checkPending();
      }, 5000);
    } else {
      console.log(this.tag, 'Not connected, waiting for network connection availability');
    }

    // Called when network changes from disconnected to connected
    if(!this.connectedSubscription) {
      this.connectedSubscription = this.network.onConnect().subscribe(() => {
        console.log(this.tag, 'network connected!');
        this.isConnected = true;
        this.checkPending();
      });
    }

    // Called when network changes from connected to disconnected
    if(!this.disconnectedSubscription) {
      this.disconnectedSubscription = this.network.onDisconnect().subscribe(() => {
        console.log(this.tag, 'network disconnected!');
        this.isConnected = false;
      });
    }
  }

  /**
   * Updates the "thingTypeCodeToSync Group
   */
  public updateThingTypeCodeToSyncGroup() {
    this.vizixService.VizixGetThingTypeGroup(this.vizixService.configurationApp.CycleCountConfiguration.ThingTypeCodeSync.value).subscribe((group) => {
      console.log(this.tag, 'Updated thingTypeCodeToSync group', group);
      this.thingTypeCodeToSyncGroup = group;
    }, (error) => {
      console.log(this.tag, 'Error updating thingTypeCodeToSync group', error);
    });
  }

  /**
   * Updates the pending count
   * @returns {Promise<number>}
   */
  public getPendingCount(): Promise<number> {

    console.log(this.tag, 'Start GetPendingCount');
    this.isGettingPendingCount = true;

    return new Promise<number>(((resolve, reject) => {
      if (this.isCordova) {
        this.db.executeSql('select count(0) from ' + this.CYCLE_COUNT_DB_TABLE_LOCAL + ' where ' +
          this.TABLE_COLUMN_STATUS + ' = ?', [UPLOAD_STATUS_PENDING_UPLOAD])
          .then(res => {
            console.log(this.tag, 'End GetPendingCount');
            this.isGettingPendingCount = false;
            if (res.rows.length > 0) {
              this.pendingCycleCountItemCount = res.rows.item(0)['count(0)'];
              console.log(this.tag, 'Total pending CycleCount items', this.pendingCycleCountItemCount);
              resolve(res.rows.item(0)['count(0)']);
            } else {
              resolve(0);
            }
          }).catch(e => {
          reject(e)
        });
      } else {
        resolve(0);
      }
    }));
  }

  public checkPending(fromTemplate?: boolean) {
    if (this.isCordova) {

      if (fromTemplate) {
        this.connectionAttempts = 0;

        if (this.retryPendingTimer != null) {
          clearTimeout(this.retryPendingTimer);
        }
      }

      if (this.isConnected) {
        if (!this.isUploading) {
          console.log(this.tag, 'checking pending');

          // First we get all the CycleCount sessions that were saved (We use the different timestamps to sort)
          this.db.executeSql('select distinct ' + this.TABLE_COLUMN_SESSION_ID + ' from ' + this.CYCLE_COUNT_DB_TABLE_LOCAL, {})
            .then(res => {
              if (res.rows.length > 0) {
                // Init recursive call

                this.uploadProgress = 1; // To show upload started in app config
                this.events.publish('backgroundSync:status', true);


                this.uploadTimestampCycleCount(res.rows, 0);

              } else {
                console.log(this.tag, 'No pending local CycleCounts');
              }
            }).catch(e => {
            console.log(this.tag, 'Error getting pending items from db', e);
          });


        } else {
          console.log(this.tag, 'CycleCount upload already in progress');
        }
      } else {
        console.log(this.tag, 'Not connected! waiting for internet connection');
      }
    } else {
      console.log(this.tag, 'No pending CycleCounts because platform is not cordova');
    }
  }

  private uploadTimestampCycleCount(timestamps: any, i: number) {
    if (this.isCordova) {
      if (i < timestamps.length) {
        let sessionId = timestamps.item(i)[this.TABLE_COLUMN_SESSION_ID];

        console.log(this.tag, 'Processing timestamp: ', sessionId);
        this.db.executeSql('select * from ' + this.CYCLE_COUNT_DB_TABLE_LOCAL +
          ' where ' + this.TABLE_COLUMN_SESSION_ID + ' = ? and ' +
          this.TABLE_COLUMN_STATUS + ' != ?', [sessionId, UPLOAD_STATUS_PENDING_REMOVE])
          .then(res => {
            console.log(this.tag, 'Timestamp: ', sessionId); // The timestamp is the date when the cyclecount items were uploaded or cyclecount finished
            console.log(this.tag, 'CycleCount size from db: ' + res.rows.length);
            // Convert response from sql to list of epcs

            let epcList: LastDetectTag[] = [];
            for (let i = 0; i < res.rows.length; i++) {
              if (res.rows.item(i).epc) {
                epcList.push(new LastDetectTag(res.rows.item(i)[this.TABLE_COLUMN_EPC], res.rows.item(i)[this.TABLE_COLUMN_LAST_DETECT_TIME]));
              }
            }

            if (epcList.length > 0) {
              // This values are the same for all the items in the SessionID, so we get only the first one
              let zone = res.rows.item(0)[this.TABLE_COLUMN_ZONE];
              let username = res.rows.item(0)[this.TABLE_COLUMN_USERNAME];
              let uuid = res.rows.item(0)[this.TABLE_COLUMN_UUID];
              let sessionID = res.rows.item(0)[this.TABLE_COLUMN_SESSION_ID]; // The session id is when the cyclecount was started

              this.uploadCycleCount(epcList, zone, username, uuid, sessionID, true).subscribe((progress) => {
                // Progress is already saved in the UploadCycleCount method
              }, (e: ErrorResponse) => {
                console.log(this.tag, 'Upload Error', e.message);
                this.resetUploadProgress(true);
                this.retryPending();
              }, () => {
                console.log(this.tag, 'Process next timestamp');
                this.uploadTimestampCycleCount(timestamps, i + 1);
              });
            } else {
              console.log(this.tag, 'Upload list is empty, nothing to upload. The epcs status may be Pending Remove');
              console.log(this.tag, 'Process next timestamp');
              this.uploadTimestampCycleCount(timestamps, i + 1);

              // this.removeNullFromDb(timestamp);
              // this.resetUploadProgress();
              // this.retryPending();
            }

          }).catch(e => console.log(this.tag, 'Error getting items for timestamp: ', sessionId))
      } else {
        console.log(this.tag, 'Upload completed!');
        this.resetUploadProgress();
        this.retryPending();
      }
    } else {
      this.resetUploadProgress();
      console.log(this.tag, 'Platform is not cordova');
    }
  }

  private removeFromDb(tagList: any[]) {
    if (this.isCordova) {

      if (tagList.length > 0) {
        console.log(this.tag, 'Removing item count', tagList.length);

        let sessionID = tagList[0].sessionID;

        let epcsListString = '(';
        for (let tag of tagList) {
          epcsListString += '\'' + tag.epc + '\'' + (tagList.indexOf(tag) == tagList.length - 1 ? '' : ',');
        }
        epcsListString += ')';

        let statement: string = 'DELETE FROM ' + this.CYCLE_COUNT_DB_TABLE_LOCAL +
          ' WHERE ' + this.TABLE_COLUMN_EPC + ' IN ' + epcsListString + ' AND ' +
          this.TABLE_COLUMN_SESSION_ID + ' = ' + sessionID;

        console.log(this.tag, 'SQLite statement', statement);

        this.db.executeSql(statement, [])
          .then(res => {
            console.log(this.tag, 'Removed from db Success', sessionID, res);
          })
          .catch(e => {
            console.log(this.tag, e);
          });

        // for (let tag of tagList) {
        //   this.db.executeSql('delete from ' + this.CYCLE_COUNT_DB_TABLE_LOCAL +
        //     ' WHERE ' + this.TABLE_COLUMN_EPC + ' = ? AND ' +
        //     this.TABLE_COLUMN_SESSION_ID + ' = ?', [tag.epc, tag.timestamp])
        //     .then(res => {
        //       // console.log(this.tag, 'Removed from db ' + tag.epc + ' (' + tag.timestamp + ')');
        //     })
        //     .catch(e => {
        //       console.log(this.tag, e);
        //     });
        // }
      } else {
        console.log(this.tag, 'No tags to remove', tagList.length);
      }
    } else {
      console.log(this.tag, 'Cant remove, platform is not cordova!');
    }
  }

  removeNullFromDb(timestamp: string) {
    if (this.isCordova) {
      this.db.executeSql('delete from ' + this.CYCLE_COUNT_DB_TABLE_LOCAL +
        ' WHERE ' + this.TABLE_COLUMN_SESSION_ID + ' = ?', [timestamp])
        .then(res => {
          console.log(this.tag, 'Removed null items from db for timestamp: ' + timestamp);
        })
        .catch(e => {
          console.log(this.tag, e);
        });
    } else {
      console.log(this.tag, 'Cant remove, platform is not cordova!');
    }
  }

  private retryPending(resetTimer?: boolean) {

    this.getPendingCount();

    if (resetTimer != null) {
      if (resetTimer) {
        this.connectionAttempts = 0;
      }
    }

    this.connectionAttempts++;

    if (this.connectionAttempts <= this.retrySyncAttempts) {
      // this.retrySyncInterval = 30000 * this.connectionAttempts;
      console.log(this.tag, 'Retrying pending after ' + this.retrySyncInterval + ' seconds, attempt ' + this.connectionAttempts);
      this.retryPendingTimer = setTimeout(() => {
        // if (this.isConnected && this.vizixService.configurationApp) {
        if (this.isConnected) {
          console.log(this.tag, 'Executing after ' + this.retrySyncInterval + ' seconds retry');
          this.checkPending();
        } else {
          console.log(this.tag, 'Not connected or logged out! waiting for internet connection');
        }
      }, this.retrySyncInterval * 1000);
    } else {
      console.log(this.tag, 'Max attempts reached, waiting for network changes or app restart');
    }
  }

  // Returns an observable with the current progress in percentage
  public saveLocally(epcs: LastDetectTag[], zone: string, username: string, uuid: string, sessionID: number, pendingStatus: string, skipRetry?: boolean): Observable<number> {

    if (this.isCordova) {
      console.log(this.tag, 'Init Save locally', {
        epcList: epcs,
        username: username,
        uuid: uuid,
        sessionID: sessionID,
        skipRetry: skipRetry
      });

      if (skipRetry == null) {
        skipRetry = false;
      }

      return Observable.create((observer: Observer<number>) => {
        console.log(this.tag, {
          epcs: epcs,
          zone: zone,
          username: username,
          uuid: uuid,
          sessionID: sessionID
        });

        let count = 0;

        // Create paginated epcs
        let arrayPaginatedEpcs: Array<Array<LastDetectTag>> = [];

        let pageCount = 0;
        let pageSize = 100;
        let pageEpc: LastDetectTag[] = [];

        for (let epc of epcs) {
          pageCount++;
          pageEpc.push(epc);

          if (pageCount == pageSize && pageSize != 0) {
            arrayPaginatedEpcs.push(pageEpc);
            pageEpc = [];
            pageCount = 0;
          }
        }

        // Last items for the last page (may be less than pageSize)
        if (pageEpc.length > 0) {
          arrayPaginatedEpcs.push(pageEpc);
        }

        for (let epcList of arrayPaginatedEpcs) {
          console.log(this.tag, 'Processing', epcList);

          // First we create the statement
          let statement: string = 'INSERT INTO ' + this.CYCLE_COUNT_DB_TABLE_LOCAL + ' VALUES ';
          for (let epc of epcList) {
            statement += '(\'' + epc.value + '\', \'' + epc.time + '\', \'' + zone + '\', \'' + username + '\', \'' + uuid + '\', \'' + sessionID + '\', \'' + pendingStatus + '\'),';
          }
          statement = statement.slice(0, -1); // Remove the last comma
          statement += ';';

          console.log(this.tag, 'SQLite statement', statement);

          this.db.executeSql(statement, [])
            .then(res => {
              count += 1;
              observer.next(Math.floor((count * pageSize / (epcs.length == 0 ? 1 : epcs.length)) * 100)); // Progress percentage
              // this.uploadProgress = Math.floor((count * pageSize / (epcs.length == 0 ? 1 : epcs.length)) * 100);

              console.log(this.tag, 'Saved Locally ', epcList);

              if (count * pageSize >= epcs.length) {
                observer.complete();
                // this.uploadProgress = 0;
                // After saved locally, try to upload them after 30 seconds
                if (!skipRetry) {
                  this.retryPending(true);
                }
                this.getPendingCount();
              }
            })
            .catch(e => {
              count += 1;
              console.error(e);
              observer.error('Error Saving Locally ' + epcList);

              if (count == epcs.length) {
                observer.complete();
                // After saved locally, try to upload them after 30 seconds
                if (!skipRetry) {
                  this.retryPending(true);
                }
              }
            });
        }
        console.log(this.tag, 'Finished saving locally');
      });
    } else {
      console.log(this.tag, 'Platform is not cordova!, cant save locally');
      return Observable.create((observer: Observer<number>) => {
        observer.next(100);
        observer.complete();
      });
    }
  }

  public uploadCycleCount(epcs: LastDetectTag[], zone: string, username: string, uuid: string, sessionId: number, fromLocalCycleCount: boolean = false): Observable<any> {

    console.log(this.tag, 'Preparing to upload', epcs.length);

    // console.log(this.tag, 'zone', zone);
    // console.log(this.tag, 'username', username);
    // console.log(this.tag, 'uuid', uuid);
    // console.log(this.tag, 'sessionId', sessionId);
    // console.log(this.tag, 'Local CC', fromLocalCycleCount);

    return Observable.create((observer: Observer<any>) => {
      if (this.isConnected) {
        if (!this.isUploading) {
          this.isUploading = true;
          this.events.publish('backgroundSync:status', true);


          // let timestamp = this.vizixService.vizixTime(); // The date the cyclecount was uploaded

          let epcsCopy: LastDetectTag[] = epcs.slice(); // The list used to control th uploaded epcs and save if error occurs
          let epcsCopyPositionsMap: Map<string, number> = new Map<string, number>();

          console.log(this.tag, 'Initial list if error:', epcsCopy);

          // if (fromLocalCycleCount) {
          //   this.username = username;
          //   this.uuid = uuid;
          //   this.sessionId = sessionId;
          // }

          // console.log(this.tag, {
          //   epcs: epcs,
          //   timestamp: timestamp,
          //   zone: zone,
          //   username: this.username,
          //   uuid: this.uuid,
          //   sessionID: this.sessionId
          // });

          // Prepare the EPCs to send in the correct format
          let body: Array<any> = [];

          let count = 0;
          for (let epc of epcs) {
            // Add each patch body to the array:
            let requestItem = new PatchThingRequestItem();

            // let upc = null;
            /*if(epc.split('|').length > 1){
              upc = epc.split("|")[1];
              epc = epc.split("|")[0];
            }*/

            // TODO: Add timestamp validation for TR 1.8.0
            requestItem.timestamp = epc.time;
            requestItem.serialNumber = epc.value;
            requestItem.name = epc.value;
            requestItem.group = this.vizixService.configurationApp.AppParametersConfiguration.Group.value;
            requestItem.thingTypeCode = this.vizixService.configurationApp.CycleCountConfiguration.ThingTypeCodeSync.value;

            requestItem.udfs = {};
            requestItem.udfs[ITEM_UDF_CYCLE_COUNT_DATE] = {value: epc.time};
            requestItem.udfs[ITEM_UDF_ZONE] = {value: zone};
            requestItem.udfs[ITEM_UDF_SOURCE] = {value: VIZIX_THING_ITEM_UDF_SOURCE};
            requestItem.udfs[ITEM_UDF_USER] = {value: username};

            if (this.vizixService.configurationApp.AppParametersConfiguration.EnableTimestamp.value) {
              requestItem.udfs[ITEM_UDF_LAST_DETECT_TIME] = {value: epc.time};
            }
            requestItem.udfs[ITEM_UDF_UUID_MOBILE] = {value: uuid};
            requestItem.udfs[ITEM_UDF_MOBILE_SYNC_SESSION_ID] = {value: String(sessionId)};
            requestItem.udfs[ITEM_UDF_CATEGORY] = {value: 'Workflow'};
            requestItem.udfs[ITEM_UDF_SUB_CATEGORY] = {value: 'CycleCount'};
            requestItem.udfs[ITEM_UDF_ACTIVITY] = {value: 'Sync'};
            requestItem.udfs[this.vizixService.configurationApp.AppParametersConfiguration.ItemEventUDF.value] = {'value': ITEM_EVENT_CYCLE_COUNT};

            if (this.vizixService.configurationApp.CycleCountConfiguration.EndPointMode.value == END_POINT_MODE_THING_BRIDGE) {
              requestItem.udfs[ITEM_UDF_TS_EDGE_IN] = {'value': '[TS_EDGE_IN]'};
            }

            body.push(requestItem);

            epcsCopyPositionsMap.set(epc.value, count);
            count++;
          }

          console.log(this.tag, 'Patch Things body: ', body);

          let bridgeCode = null;
          let thingTypeCode = null;

          if (this.vizixService.configurationApp.CycleCountConfiguration.EndPointMode.value == END_POINT_MODE_THING_BRIDGE) {
            bridgeCode = this.vizixService.configurationApp.CycleCountConfiguration.BridgeCode.value;
            thingTypeCode = this.vizixService.configurationApp.CycleCountConfiguration.ThingTypeCodeSync.value;
          }

          if (this.vizixService.configurationApp.CycleCountConfiguration) {

            let totalUploadedList: Array<any> = [];

            this.vizixClientService.patchThings(body,
              this.vizixService.configurationApp.CycleCountConfiguration.SyncInterval.value,
              bridgeCode,
              thingTypeCode,
              THING_BRIDGE_SOURCE_CYCLE_COUNT,
              this.vizixService.configurationApp.AppParametersConfiguration.EnableTimestamp.value).subscribe(
              (response: any) => {

                // Now delete uploaded list from DB
                let epcListToRemove: any[] = [];
                let epcListToAddToDb: any[] = [];

                // Handle the resonse in a different way depending if was things or thingBridge
                if (this.vizixService.configurationApp.CycleCountConfiguration.EndPointMode.value == END_POINT_MODE_THINGS) {

                  // If from local cycle count, then remove the tags that were uploaded successfully and save locally the ones with error
                  if (fromLocalCycleCount) {
                    for (let uploadedItemSerial in response.uploadedList) {
                      epcListToRemove.push(
                        {
                          epc: response.uploadedList[uploadedItemSerial].thing.serial,
                          timestamp: response.uploadedList[uploadedItemSerial].fields.lastDetectTime,
                          sessionID: sessionId
                        });

                      // Remove from list
                      let index = epcsCopy.indexOf(response.uploadedList[uploadedItemSerial].thing.serial, 0);
                      if (index > -1) {
                        epcsCopy.splice(index, 1);
                      }
                    }
                    console.log(this.tag, 'Resulting list if error:', epcsCopy);
                    this.removeFromDb(epcListToRemove);

                    // Check for errors and log
                    for (let errorItemSerial in response.errorList) {
                      epcListToAddToDb.push(errorItemSerial);
                    }
                    if (epcListToAddToDb.length > 0) {
                      console.log(this.tag, 'With Error list', epcListToAddToDb);
                    }

                  } else { // If not from local cycle count, then save to local db the tags that had error uploading
                    for (let uploadedItemSerial in response.uploadedList) {
                      // Remove from list
                      let index = epcsCopy.indexOf(response.uploadedList[uploadedItemSerial].thing.serial, 0);
                      if (index > -1) {
                        epcsCopy.splice(index, 1);
                      }
                    }
                    console.log(this.tag, 'Resulting list if error:', epcsCopy);

                    for (let errorItemSerial in response.errorList) {
                      epcListToAddToDb.push(errorItemSerial);
                    }
                    if (epcListToAddToDb.length > 0) {
                      console.log(this.tag, 'With Error list', epcListToAddToDb);
                      for (let uploadedItemSerial in response.uploadedList) {
                        epcListToRemove.push(
                          {
                            epc: response.uploadedList[uploadedItemSerial].thing.serial,
                            timestamp: response.uploadedList[uploadedItemSerial].fields.lastDetectTime,
                            sessionID: sessionId
                          });

                        // Remove from list
                        let index = epcsCopy.indexOf(response.uploadedList[uploadedItemSerial].thing.serial, 0);
                        if (index > -1) {
                          epcsCopy.splice(index, 1);
                        }
                      }
                      console.log(this.tag, 'Resulting list if error:', epcsCopy);
                      this.saveLocally(epcListToAddToDb, zone, username, uuid, sessionId, UPLOAD_STATUS_PENDING_UPLOAD, true).subscribe();
                    }
                  }
                } else { // Handle response from thingbridge
                  console.log(this.tag, 'Response for thingBridge request', response);
                  for (let uploadedItem of response.uploadedList) {
                    epcListToRemove.push(
                      {
                        epc: uploadedItem.epc,
                        timestamp: uploadedItem.time,
                        sessionID: sessionId
                      });

                    // Add Uploaded List items to the total list
                    totalUploadedList.push(uploadedItem);
                  }

                  console.log(this.tag, 'Resulting list if error:', epcsCopy);

                  if (this.pendingRemoveSaveEnabled) {
                    if (!fromLocalCycleCount) {
                      // We save locally every item
                      this.saveLocally(response.uploadedList, zone, username, uuid, sessionId, UPLOAD_STATUS_PENDING_REMOVE, true).subscribe();
                    } else {
                      this.updateStatus(response.uploadedList, sessionId, UPLOAD_STATUS_PENDING_UPLOAD, UPLOAD_STATUS_PENDING_REMOVE);
                    }
                  } else {
                    // Remove the items, because it will behave the same way as Things endpoint mode
                    if (fromLocalCycleCount) {
                      this.removeFromDb(epcListToRemove);
                    }
                  }
                }

                let addedProgress = parseFloat(response.progress) * epcs.length / (fromLocalCycleCount ? (this.pendingCycleCountItemCount == 0 ? 1 : this.pendingCycleCountItemCount) : epcs.length);
                let totalProgress = this.uploadProgress + addedProgress;
                if (totalProgress > 100) {
                  this.uploadProgress = 100;
                } else {
                  this.uploadProgress = totalProgress;
                }

                console.log(this.tag + '.progress', 'Patch Things page response: ', response);
                console.log(this.tag + '.progress', 'EpcList Length:', epcs.length);
                console.log(this.tag + '.progress', 'PendingCycleCountItemCount:', this.pendingCycleCountItemCount);
                console.log(this.tag + '.progress', 'Patch Progress:', response.progress);
                console.log(this.tag + '.progress', 'Added Progress:', addedProgress);
                console.log(this.tag + '.progress', 'Total Progress:', this.uploadProgress);
                observer.next({progress: this.uploadProgress, errorCount: epcListToAddToDb.length});

              }, (error) => {

                // An error occurred in a request, we save the remaining EPCs
                if (!fromLocalCycleCount) {

                  // If error, we remove the uploaded items from the epcsCopy // TODO: Improve performance
                  for (let uploadedTag of totalUploadedList) {
                    // Remove from list
                    let index = epcsCopyPositionsMap.get(uploadedTag.epc);
                    if (index || index == 0) {
                      epcsCopy[index].toDelete = true;
                    }
                  }

                  for (let i = epcsCopy.length - 1; i >= 0; i = i - 1) {
                    if (epcsCopy[i].toDelete) {
                      epcsCopy.splice(i, 1);
                    }
                  }

                  this.saveLocally(epcsCopy, zone, username, uuid, sessionId, UPLOAD_STATUS_PENDING_UPLOAD).subscribe();
                }
                console.log(this.tag, 'Error: ', error);
                observer.error(new ErrorResponse(JSON.stringify(error), UNKNOWN_ERROR));
              }, () => {
                console.log(this.tag, 'Complete Upload');
                // Now check for incomplete upload items after 30 seconds

                // this.retryPending(!fromLocalCycleCount);
                // this.getPendingCount().then((count) => {
                //   // If the count is still > 0, then do the process again
                //   if(count > 0){
                //     this.checkPending();
                //   }
                // });

                if (!fromLocalCycleCount) {
                  console.log(this.tag, 'Upload completed!');
                  this.resetUploadProgress();
                }

                observer.complete();
              }
            );
          }
        } else {
          observer.error(new ErrorResponse('CycleCount upload is already in progress, saving locally to queue', CYCLE_COUNT_ALREADY_UPLOADING));
          if (!fromLocalCycleCount) {
            this.saveLocally(epcs, zone, username, uuid, sessionId, UPLOAD_STATUS_PENDING_UPLOAD, true).subscribe();
          }
        }
      } else {
        observer.error(new ErrorResponse('Not connected to internet', NO_INTERNET_CONNECTION));
        if (!fromLocalCycleCount) {
          this.saveLocally(epcs, zone, username, uuid, sessionId, UPLOAD_STATUS_PENDING_UPLOAD, true).subscribe();
        }
      }
    });
  }

  /**
   * Changes the status for an specific group of items in database
   * @param {string[]} epcs list of items
   * @param {string} sessionId to differentiate if the items were read multiple times in different sessions
   * @param {string} oldStatus what to search
   * @param {string} newStatus what to replace
   */
  updateStatus(epcs: string[], sessionId: number, oldStatus: string, newStatus: string) {

    if (this.isCordova) {
      let epcsListString = '(';
      for (let epc of epcs) {
        epcsListString += '\'' + epc + '\'' + (epcs.indexOf(epc) == epcs.length - 1 ? '' : ',');
      }
      epcsListString += ')';

      let statement: string = 'UPDATE ' + this.CYCLE_COUNT_DB_TABLE_LOCAL +
        ' SET ' + this.TABLE_COLUMN_STATUS + ' = \'' + newStatus +
        '\' WHERE ' + this.TABLE_COLUMN_STATUS + ' = \'' + oldStatus +
        '\' AND ' + this.TABLE_COLUMN_SESSION_ID + ' = \'' + sessionId +
        '\' AND ' + this.TABLE_COLUMN_EPC + ' IN ' + epcsListString;

      console.log(this.tag, 'SQLite statement', statement);

      this.db.executeSql(statement, [])
        .then(res => {
          console.log(this.tag, 'SQL result', res);
        })
        .catch(e => {
          console.log(this.tag, 'SQL error', e);
        });
    } else {
      console.log(this.tag, 'Cant update, platform is not cordova!');
    }

  }

  resetUploadProgress(skipConnectionAttempts ?: boolean) {
    this.isUploading = false;
    this.uploadProgress = 0;
    this.events.publish('backgroundSync:status', false);
    if (!skipConnectionAttempts) {
      this.connectionAttempts = 0;
    }
  }

  executeCycleCountReport(params: {
    success?: boolean,
    selectedZone: ZoneType,
    selectedDepartment: DepartmentsZones,
    storeID: string,
    uuid: string
  }): Observable<TableScriptReportResponse> {

    return Observable.create((observer: Observer<TableScriptReportResponse>) => {
      let reportBody = {};
      reportBody[this.vizixService.configurationApp.CycleCountConfiguration.FilterLabelZoneType.value] = String(params.selectedZone.idZone);
      reportBody[this.vizixService.configurationApp.CycleCountConfiguration.FilterLabelStoreID.value] = params.storeID;
      if (!params.selectedDepartment.ALL) {
        reportBody[this.vizixService.configurationApp.CycleCountConfiguration.FilterLabelDepartment.value] = params.selectedDepartment.DeptName;
      }

      // Only if it is the second time, add the filters:
      if (params.success) {
        reportBody['ShowLastCycleCount'] = true;
        reportBody['UUIDMobile'] = params.uuid;
      }

      this.vizixService.VizixPostReportExecutionCycleCount(reportBody)
        .subscribe(
          (response: TableScriptReportResponse) => {

            if (typeof response.data != 'undefined' && (params.success || typeof response.extra != 'undefined')) {

              observer.next(response);

            } else {

              observer.error({
                message: 'Report has an error in the response (data or extra missing?)',
                code: ErrorCode.REPORT_ERROR
              });
            }
          }, (e) => {
            observer.error({message: e, code: ErrorCode.SERVER_ERROR});
          }, () => {
            console.log(this.tag, 'complete');
            observer.complete();
          }
        );
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/cyclecount.service.ts